import type { SortBy } from '@features/instructors/types/sort.types';
import type { SortDirection } from '@shared/types/sort.types';

import { useOrganizations } from '@contexts/organizations-provider';
import * as React from 'react';

import type {
  InstructorsDataContextValue,
  InstructorsDataProviderProps,
} from './InstructorsDataContext.types';

import { useInstructors } from '../../hooks/use-instructors/useInstructors';
import { useInstructorsFilter } from '../filter/useInstructorsFilter';
import {
  INITIAL_DATA_STATE,
  INITIAL_INSTRUCTORS_DATA,
} from './InstructorsDataProvider.utils';

const InstructorsDataContext = React.createContext<
  InstructorsDataContextValue | undefined
>(undefined);

const InstructorsDataProvider = React.memo(
  ({
    children,
  }: Readonly<InstructorsDataProviderProps>): React.ReactElement => {
    const { selectedOrganization } = useOrganizations();
    const { filters } = useInstructorsFilter();

    const [pageSize, setPageSize] = React.useState<number>(
      INITIAL_DATA_STATE.pageSize
    );
    const [pageIndex, setPageIndex] = React.useState<number>(
      INITIAL_DATA_STATE.pageIndex
    );
    const [sortBy, setSortBy] = React.useState<SortBy>(
      INITIAL_DATA_STATE.sortBy
    );
    const [sortDirection, setSortDirection] = React.useState<SortDirection>(
      INITIAL_DATA_STATE.sortDirection
    );

    const filterKey = JSON.stringify(filters);
    const prevFilterKey = React.useRef(filterKey);

    React.useEffect(() => {
      if (prevFilterKey.current !== filterKey) {
        setPageIndex(0);
        prevFilterKey.current = filterKey;
      }
    }, [filterKey]);

    const { data, isLoading, isFetching, isError, refetch } = useInstructors({
      customerId: selectedOrganization?.customerId ?? '',
      pageSize,
      pageIndex,
      sortBy,
      sortDirection,
      searchText: filters.searchText,
      programs: filters.programs,
      locations: filters.locations,
      disciplines: filters.disciplines,
    });

    const handlePageIndexChange = React.useCallback(
      (newPageIndex: number): void => {
        setPageIndex(newPageIndex);
      },
      []
    );

    const handlePageSizeChange = React.useCallback(
      (newPageSize: number): void => {
        setPageSize(newPageSize);
        setPageIndex(0);
      },
      []
    );

    const handleSortChange = React.useCallback(
      (newSortBy: SortBy, direction: SortDirection): void => {
        setSortBy(newSortBy);
        setSortDirection(direction);
        setPageIndex(0);
      },
      []
    );

    const value: InstructorsDataContextValue = React.useMemo(
      () => ({
        instructors: {
          data: data ?? INITIAL_INSTRUCTORS_DATA,
          isLoading,
          isFetching,
          isError,
        },
        pageSize,
        pageIndex,
        sortBy,
        sortDirection,
        handlePageIndexChange,
        handlePageSizeChange,
        handleSortChange,
        refetch,
      }),
      [
        data,
        isLoading,
        isFetching,
        isError,
        pageSize,
        pageIndex,
        sortBy,
        sortDirection,
        handlePageIndexChange,
        handlePageSizeChange,
        handleSortChange,
        refetch,
      ]
    );

    return (
      <InstructorsDataContext value={value}>
        {children}
      </InstructorsDataContext>
    );
  }
);

InstructorsDataProvider.displayName = 'InstructorsDataProvider';

export { InstructorsDataProvider, InstructorsDataContext };
